import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';

import { datasetToAttributes } from '../../utils';
import { Text } from '../Text';
import type { CSSResize } from './TextArea.style';
import { StyledCounter, StyledError, StyledTextArea, StyledWrapper } from './TextArea.style';

export interface TextAreaProps {
  name: string;
  readOnly?: boolean;
  disabled?: boolean;
  placeholder?: string;
  defaultValue?: string;
  errorMessage?: string;
  cols?: number;
  rows?: number;
  dataset?: Record<string, string>;
  count?: {
    show: boolean;
    limit: number;
    min?: number;
    exceedFormatter?: (string: string, options: { limit: number }) => string;
  };
  value?: string;
  resize?: CSSResize;
  autoResize?: { min: number; max: number };
  onChange?: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  maxHeight?: number;
}

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      name,
      readOnly = false,
      disabled = false,
      placeholder,
      defaultValue,
      errorMessage,
      cols,
      rows,
      count,
      value,
      resize = 'vertical',
      autoResize = null,
      onChange,
      dataset,
    },
    ref
  ) => {
    const theme = useTheme();
    const [textLength, setTextLength] = useState(value?.length || defaultValue?.length || 0);
    const previousValueRef = useRef(value);

    const dataAttributes = useMemo(() => datasetToAttributes(dataset), [dataset]);

    const updateHeight = useCallback(() => {
      if (autoResize && resize !== 'none') {
        const textAreaElement = (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current;

        if (textAreaElement) {
          textAreaElement.style.height = 'auto';
          textAreaElement.style.height = `${Math.min(
            Math.max(textAreaElement.scrollHeight, autoResize.min),
            autoResize.max
          )}px`;
        }
      }
    }, [autoResize, resize, ref]);

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setTextLength(() => {
          const newValue = event.target.value;

          if (count?.exceedFormatter && count?.limit && newValue.length > count?.limit) {
            event.target.value = count.exceedFormatter(newValue, {
              limit: count.limit,
            });

            return count.limit;
          }

          return newValue.length;
        });

        onChange && onChange(event);
      },
      [onChange, autoResize, resize, count, ref]
    );

    useEffect(
      function updateTextLength() {
        setTextLength(defaultValue?.length || 0);
      },
      [defaultValue]
    );

    useLayoutEffect(() => {
      if (value !== undefined && value !== previousValueRef.current) {
        previousValueRef.current = value;
        updateHeight();
      }
    }, [value, updateHeight]);

    return (
      <StyledWrapper>
        <StyledTextArea
          {...dataAttributes}
          ref={ref}
          name={name}
          readOnly={readOnly}
          disabled={disabled}
          cols={cols}
          rows={rows}
          placeholder={placeholder}
          defaultValue={defaultValue}
          $isError={
            !!errorMessage ||
            (textLength ?? 0) > (count?.limit ?? Number.MAX_SAFE_INTEGER) ||
            (textLength ?? 0) < (count?.min ?? Number.MIN_SAFE_INTEGER)
          }
          value={value}
          resize={resize}
          onChange={handleChange}
        />

        {count?.show && (
          <StyledCounter>
            <Text
              size={'Body'}
              color={
                textLength > count.limit ? theme.Color.Feedback.Alert : theme.Color.Neutrals.Text
              }
              showTooltip={true}
            >
              {textLength} / {count.limit}
            </Text>
          </StyledCounter>
        )}

        {errorMessage && (
          <StyledError>
            <Text size={'Small'} color={theme.Color.Feedback.Alert} showTooltip={true}>
              {errorMessage}
            </Text>
          </StyledError>
        )}
      </StyledWrapper>
    );
  }
);

TextArea.displayName = 'TextArea';
